import { useState, useEffect, useRef } from 'react';
import { buildTeamApiService } from '../services/buildTeamApi';

/**
 * Fetches schematic download bytes (inline for screenshots) as a blob URL for previews.
 */
export function useBuildSchematicBlobUrl(schematicId: string | undefined | null): {
  url: string | null;
  loading: boolean;
  error: string | null;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!schematicId);
  const [error, setError] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    const revoke = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    if (!schematicId) {
      revoke();
      setUrl(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    revoke();
    setUrl(null);
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await buildTeamApiService.downloadSchematic(schematicId);
        const blob = await res.blob();
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;
        setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load');
          setUrl(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      revoke();
    };
  }, [schematicId]);

  return { url, loading, error };
}
